import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsCategories, FinTechOpsMessages, InitialTicketsTabData } from '../../FinTechOpsConstants';
import { TicketParsedDetails, TicketsDashboard } from '../../tickets/TicketsInterfaces';
import { useTicketsData } from '../hooks/useTicketsData';

export interface TicketsContextValue {
  finTechOpsTicketDataStatus: FiltersStatus;
  setFinTechOpsTicketDataStatus: (s: FiltersStatus) => void;
  finTechOpsTicketsData: TicketParsedDetails;
  setFinTechOpsTicketsData: (d: TicketParsedDetails) => void;
  finTechOpsTicketDashboard: TicketsDashboard;
  setFinTechOpsTicketDashboard: (d: TicketsDashboard) => void;
}

const EMPTY_PARSED = {} as TicketParsedDetails;

const defaultValue: TicketsContextValue = {
  finTechOpsTicketDataStatus: 'loading',
  setFinTechOpsTicketDataStatus: () => {},
  finTechOpsTicketsData: EMPTY_PARSED,
  setFinTechOpsTicketsData: () => {},
  finTechOpsTicketDashboard: InitialTicketsTabData,
  setFinTechOpsTicketDashboard: () => {}
};

const TicketsContext = createContext<TicketsContextValue>(defaultValue);

export const useTickets = () => useContext(TicketsContext);

interface Props {
  children: React.ReactNode;
}

export const TicketsProvider = ({ children }: Props) => {
  const { data, loading, error } = useTicketsData();
  const { displayNotification } = useNotification();
  const [parsed, setParsed] = useState<TicketParsedDetails>(EMPTY_PARSED);
  const [dashboard, setDashboard] = useState<TicketsDashboard>(InitialTicketsTabData);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error(`Unable to load ${FinTechOpsCategories.tickets} data`, error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      return;
    }
    if (data) {
      setParsed(data);
      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsTicketDataStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setFinTechOpsTicketsData = useCallback((d: TicketParsedDetails) => setParsed(d), []);
  const setFinTechOpsTicketDashboard = useCallback((d: TicketsDashboard) => setDashboard(d), []);

  const value = useMemo<TicketsContextValue>(
    () => ({
      finTechOpsTicketDataStatus: status,
      setFinTechOpsTicketDataStatus,
      finTechOpsTicketsData: parsed,
      setFinTechOpsTicketsData,
      finTechOpsTicketDashboard: dashboard,
      setFinTechOpsTicketDashboard
    }),
    [status, parsed, dashboard, setFinTechOpsTicketDataStatus, setFinTechOpsTicketsData, setFinTechOpsTicketDashboard]
  );

  return <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>;
};
